import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit } from '@angular/core';

/** Minimalna szerokość uchwytu, żeby przy bardzo długiej treści dało się go jeszcze złapać. */
const MIN_THUMB_WIDTH = 24;
/** Tolerancja na zaokrąglenia szerokości przy skalowaniu strony (zoom, DPI). */
const OVERFLOW_TOLERANCE = 1;

const CONTENT_SELECTOR = '.overlay-scroll-content';
const THUMB_SELECTOR = '.overlay-scroll-thumb';
const OVERFLOWING_CLASS = 'overlay-scroll-overflowing';
const DRAGGING_CLASS = 'overlay-scroll-dragging';

/**
 * Poziome przewijanie treści z własnym, nakładkowym uchwytem — w zamian za natywny pasek przewijania.
 *
 * Natywny pasek zajmuje miejsce w układzie, a ile dokładnie, zależy od systemu i przeglądarki
 * (Chrome/Linux ~15px, `scrollbar-width: thin` ~11px, overlay na macOS 0px). Na liście renderowanej
 * wirtualnym scrollem jest to nie do przyjęcia: `FixedSizeVirtualScrollStrategy` pozycjonuje wiersze
 * przez `translateY(index × itemSize)`, więc każdy wiersz musi mieć identyczną wysokość — a pasek
 * pojawiający się tylko w wierszach z długim tekstem tę wysokość zmienia.
 *
 * Dlatego natywny pasek jest ukrywany w stylach (`scrollbar-width: none` i `::-webkit-scrollbar`),
 * a jego rolę pełni uchwyt pozycjonowany absolutnie, czyli poza układem — bez wpływu na wysokość.
 * Samo przewijanie zostaje natywne (touchpad, shift+kółko, klawiatura), więc nic nie emulujemy.
 *
 * Oczekiwana struktura hosta (statyczna, więc wyszukiwana raz w `ngOnInit`):
 *
 * `<div [appOverlayScrollbar]="klucz">` — `position: relative`
 * `<div class="overlay-scroll-content">` — przewijana treść, `overflow-x: auto` z ukrytym paskiem
 * `<div class="overlay-scroll-thumb">` — uchwyt, `position: absolute`
 *
 * W wartość wejściową idzie identyfikator treści (np. to samo, co w `trackBy` listy). Jego zmiana
 * znaczy, że host dostał inną treść — na przykład po recyklingu wiersza przez wirtualny scroll —
 * i wtedy przewinięcie poprzedniej treści trzeba wyzerować.
 *
 * Host dostaje klasę `overlay-scroll-overflowing`, gdy jest co przewijać (inaczej uchwyt nie ma
 * sensu i style powinny go ukryć), oraz `overlay-scroll-dragging` na czas przeciągania.
 *
 * Nasłuchy i zapisy do DOM idą poza strefą Angulara: przy tysiącach wierszy przewijanie nie może
 * uruchamiać change detection.
 */
@Directive({ selector: '[appOverlayScrollbar]' })
export class OverlayScrollbarDirective implements OnChanges, OnInit, OnDestroy {

  /** Identyfikator przewijanej treści — jego zmiana oznacza, że host pokazuje już coś innego. */
  @Input('appOverlayScrollbar')
  contentKey: unknown;

  private content: HTMLElement | null = null;
  private thumb: HTMLElement | null = null;

  private resizeObserver: ResizeObserver | null = null;

  private scrollListener: (() => void) | null = null;
  private pointerDownListener: ((event: PointerEvent) => void) | null = null;
  private pointerMoveListener: ((event: PointerEvent) => void) | null = null;
  private pointerUpListener: ((event: PointerEvent) => void) | null = null;

  private syncFrame: number | null = null;

  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) { }

  ngOnChanges(): void {
    // Treść jest inna, więc przewinięcie poprzedniej nie ma już sensu. Uwaga: `ngOnChanges` biegnie
    // przed aktualizacją interpolacji w środku hosta, dlatego wymiary liczymy dopiero w `scheduleSync`,
    // czyli po zakończeniu change detection.
    if (this.content != null)
      this.content.scrollLeft = 0;
    this.scheduleSync();
  }

  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.content = host.querySelector(CONTENT_SELECTOR);
    this.thumb = host.querySelector(THUMB_SELECTOR);
    if (this.content == null || this.thumb == null)
      return;

    this.scrollListener = () => this.scheduleSync();
    this.pointerDownListener = event => this.onPointerDown(event);
    this.pointerMoveListener = event => this.onPointerMove(event);
    this.pointerUpListener = event => this.onPointerUp(event);

    this.zone.runOutsideAngular(() => {
      this.content.addEventListener('scroll', this.scrollListener, { passive: true });
      this.thumb.addEventListener('pointerdown', this.pointerDownListener);
      this.resizeObserver = new ResizeObserver(() => this.scheduleSync());
      this.resizeObserver.observe(this.content);
      this.scheduleSync();
    });
  }

  ngOnDestroy(): void {
    if (this.syncFrame != null)
      cancelAnimationFrame(this.syncFrame);
    if (this.content != null && this.scrollListener != null)
      this.content.removeEventListener('scroll', this.scrollListener);
    if (this.thumb != null && this.pointerDownListener != null)
      this.thumb.removeEventListener('pointerdown', this.pointerDownListener);
    this.stopDrag();
    this.resizeObserver?.disconnect();
  }

  /**
   * Zdarzeń scrolla i zmian rozmiaru bywa wiele na jedną klatkę, a uchwyt i tak przesuwamy raz —
   * dlatego przeliczenie jest sklejane do najbliższej klatki.
   */
  private scheduleSync(): void {
    if (this.syncFrame != null)
      return;
    // Planowanie zawsze poza strefą Angulara — `ngOnChanges` woła to z jej wnętrza, a wtedy klatka
    // wracałaby do strefy i wymuszała change detection dla każdego wiersza z osobna.
    this.zone.runOutsideAngular(() => {
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    });
  }

  private sync(): void {
    if (this.content == null || this.thumb == null)
      return;

    const view = this.content.clientWidth;
    const total = this.content.scrollWidth;
    if (total - view <= OVERFLOW_TOLERANCE) {
      this.el.nativeElement.classList.remove(OVERFLOWING_CLASS);
      return;
    }

    this.el.nativeElement.classList.add(OVERFLOWING_CLASS);
    const width = this.thumbWidth(view, total);
    const travel = view - width;
    const progress = travel <= 0 ? 0 : this.content.scrollLeft / (total - view);
    this.thumb.style.width = `${width}px`;
    // Przez `transform`, a nie `left`, żeby przesunięcie uchwytu nie wymuszało układu na nowo.
    this.thumb.style.transform = `translateX(${Math.round(travel * progress)}px)`;
  }

  /** Szerokość uchwytu proporcjonalna do widocznej części treści, jak w natywnym pasku. */
  private thumbWidth(view: number, total: number): number {
    return Math.max(MIN_THUMB_WIDTH, Math.round(view * view / total));
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.content == null || this.thumb == null || event.button !== 0)
      return;

    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.content.scrollLeft;
    this.thumb.setPointerCapture(event.pointerId);
    if (this.pointerMoveListener != null && this.pointerUpListener != null) {
      this.thumb.addEventListener('pointermove', this.pointerMoveListener);
      this.thumb.addEventListener('pointerup', this.pointerUpListener);
      this.thumb.addEventListener('pointercancel', this.pointerUpListener);
    }
    this.el.nativeElement.classList.add(DRAGGING_CLASS);
    // Bez tego przeciąganie uchwytu zaznacza tekst w komórkach pod nim.
    event.preventDefault();
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.content == null || event.pointerId !== this.dragPointerId)
      return;

    const view = this.content.clientWidth;
    const total = this.content.scrollWidth;
    const travel = view - this.thumbWidth(view, total);
    if (travel <= 0)
      return;

    // Uchwyt przebiega `travel`, treść `total - view`, więc ruch kursora skalujemy tą proporcją.
    const shift = (event.clientX - this.dragStartX) * ((total - view) / travel);
    this.content.scrollLeft = this.dragStartScrollLeft + shift;
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.dragPointerId)
      return;
    this.stopDrag();
  }

  private stopDrag(): void {
    if (this.dragPointerId == null)
      return;

    if (this.thumb != null) {
      if (this.thumb.hasPointerCapture(this.dragPointerId))
        this.thumb.releasePointerCapture(this.dragPointerId);
      if (this.pointerMoveListener != null && this.pointerUpListener != null) {
        this.thumb.removeEventListener('pointermove', this.pointerMoveListener);
        this.thumb.removeEventListener('pointerup', this.pointerUpListener);
        this.thumb.removeEventListener('pointercancel', this.pointerUpListener);
      }
    }
    this.el.nativeElement.classList.remove(DRAGGING_CLASS);
    this.dragPointerId = null;
  }

}
